import React, { useState, useEffect } from 'react';
import { 
  Modal, 
  Checkbox, 
  List, 
  message, 
  Spin, 
  Button, 
  Space, 
  Typography, 
  Alert,
  Tag,
  Divider
} from 'antd';
import { 
  ReloadOutlined, 
  FileOutlined, 
  CheckCircleOutlined,
  SettingOutlined 
} from '@ant-design/icons';
import axios from 'axios';

const { Title, Text } = Typography;

const STORAGE_KEY = 'devis_drop_selected_files';

const DevisDropFileSelector = ({ visible, onClose, onImport }) => {
  const [files, setFiles] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);

  // Load selected files from localStorage on mount
  useEffect(() => {
    const savedSelection = localStorage.getItem(STORAGE_KEY);
    if (savedSelection) {
      try {
        const parsed = JSON.parse(savedSelection);
        setSelectedFiles(parsed);
      } catch (e) {
        console.error('Error parsing saved file selection:', e);
      }
    }
  }, []);

  // Fetch files when modal opens
  useEffect(() => {
    if (visible) {
      fetchFiles();
    }
  }, [visible]);

  const fetchFiles = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/import/devis-drop/files`
      );
      
      if (response.data.success) {
        setFiles(response.data.files || []);
        message.success(`${response.data.count} fichier(s) trouvé(s)`);
      } else {
        message.error('Échec de la récupération des fichiers');
      }
    } catch (error) {
      console.error('Error fetching files:', error);
      message.error(`Erreur: ${error.response?.data?.message || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleFileToggle = (fileName) => {
    setSelectedFiles((prev) => {
      const newSelection = prev.includes(fileName)
        ? prev.filter((f) => f !== fileName)
        : [...prev, fileName];
      
      // Save to localStorage
      localStorage.setItem(STORAGE_KEY, JSON.stringify(newSelection));
      
      return newSelection;
    });
  };

  const handleSelectAll = () => {
    const allFileNames = files.map((f) => f.fileName);
    setSelectedFiles(allFileNames);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(allFileNames));
    message.success('Tous les fichiers sélectionnés');
  };

  const handleDeselectAll = () => {
    setSelectedFiles([]);
    localStorage.setItem(STORAGE_KEY, JSON.stringify([]));
    message.success('Sélection effacée');
  };

  const handleImport = async () => {
    setImporting(true);
    try {
      let response;
      
      if (selectedFiles.length === 0) {
        // Import all files if none are selected
        response = await axios.post(
          `${process.env.REACT_APP_API_BASE_URL}/import/devis-drop/run`
        );
        message.success('Import de tous les fichiers exécuté avec succès');
      } else {
        // Import only selected files
        response = await axios.post(
          `${process.env.REACT_APP_API_BASE_URL}/import/devis-drop/import-selected`,
          { selectedFiles }
        );
        message.success(response.data.message);
      }
      
      if (onImport) {
        onImport(response.data.processedFiles || []);
      }
      onClose();
    } catch (error) {
      console.error('Import error:', error);
      message.error(`Erreur d'import: ${error.response?.data?.message || error.message}`);
    } finally {
      setImporting(false);
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes || bytes === 0) return 'N/A';
    const kb = bytes / 1024;
    if (kb < 1024) return `${kb.toFixed(2)} KB`;
    const mb = kb / 1024;
    return `${mb.toFixed(2)} MB`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleString('fr-FR');
    } catch (e) {
      return 'N/A';
    }
  };

  return (
    <Modal
      title={
        <Space>
          <SettingOutlined />
          <span>Configuration Import Devis Modifiés</span>
        </Space>
      }
      open={visible}
      onCancel={onClose}
      width={800}
      footer={[
        <Button key="refresh" icon={<ReloadOutlined />} onClick={fetchFiles} loading={loading}>
          Actualiser
        </Button>,
        <Button key="cancel" onClick={onClose}>
          Annuler
        </Button>,
        <Button
          key="import"
          type="primary"
          icon={<CheckCircleOutlined />}
          onClick={handleImport}
          loading={importing}
        >
          {selectedFiles.length === 0 ? 'Importer Tous' : `Importer (${selectedFiles.length})`}
        </Button>,
      ]}
    >
      <Space direction="vertical" style={{ width: '100%' }} size="large">
        <Alert
          message="Sélection de fichiers pour import (optionnel)"
          description="Sélectionnez les fichiers DBF spécifiques à importer depuis Celer. Si aucun fichier n'est sélectionné, tous les fichiers seront importés. Votre sélection sera sauvegardée automatiquement."
          type="info"
          showIcon
        />

        <Space>
          <Button size="small" onClick={handleSelectAll}>
            Tout sélectionner
          </Button>
          <Button size="small" onClick={handleDeselectAll}>
            Tout désélectionner
          </Button>
          <Tag color="blue">
            {selectedFiles.length} / {files.length} sélectionné(s)
          </Tag>
        </Space>

        <Divider style={{ margin: '8px 0' }} />

        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <Spin size="large" tip="Chargement des fichiers..." />
          </div>
        ) : files.length === 0 ? (
          <Alert
            message="Aucun fichier trouvé"
            description="Aucun fichier n'est disponible dans le dossier devis-drop sur le serveur FTP."
            type="warning"
            showIcon
          />
        ) : (
          <List
            bordered
            dataSource={files}
            renderItem={(file) => {
              const isSelected = selectedFiles.includes(file.fileName);
              return (
                <List.Item
                  style={{
                    backgroundColor: isSelected ? '#e6f7ff' : 'white',
                    cursor: 'pointer',
                    transition: 'background-color 0.3s',
                  }}
                  onClick={() => handleFileToggle(file.fileName)}
                >
                  <List.Item.Meta
                    avatar={
                      <Checkbox
                        checked={isSelected}
                        onChange={() => handleFileToggle(file.fileName)}
                      />
                    }
                    title={
                      <Space>
                        <FileOutlined />
                        <Text strong>{file.fileName}</Text>
                        {isSelected && <Tag color="green">Sélectionné</Tag>}
                      </Space>
                    }
                    description={
                      <Space split="|">
                        <Text type="secondary">Taille: {formatFileSize(file.size)}</Text>
                        <Text type="secondary">Modifié: {formatDate(file.modifiedTime)}</Text>
                      </Space>
                    }
                  />
                </List.Item>
              );
            }}
            style={{ maxHeight: '400px', overflowY: 'auto' }}
          />
        )}

        {selectedFiles.length > 0 && (
          <Alert
            message="Fichiers sélectionnés"
            description={
              <div>
                {selectedFiles.map((fileName, index) => (
                  <Tag key={index} color="blue" style={{ margin: '2px' }}>
                    {fileName}
                  </Tag>
                ))}
              </div>
            }
            type="success"
            showIcon
          />
        )}
      </Space>
    </Modal>
  );
};

export default DevisDropFileSelector;

